import { MAX_BATCH_PRICE, MIN_BATCH_PRICE } from "@academy-context/shared/domain/constants/batch-price";
import type { UseFormReturn } from "react-hook-form";
import type { FormSubmit } from "@shared-kernel/primary/shared/api-errors/action-form";
import type { RequestState } from "@redux/state/shared-types";
import { Button } from "@components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";
import { Input } from "@components/ui/input";
import { Loader2 } from "lucide-react";
import { Alert } from "@shared-kernel/primary/shared/alert";
import { UploadButton } from "@shared-kernel/primary/shared/upload-button/upload-button";
import { PopoverCalendar } from "@shared-kernel/primary/shared/calendar/popover-calendar";
import { getMinFiscalPeriodDate } from "@shared-kernel/primary/shared/date/min-fiscal-period-date";
import {
  SUBCONTRACTING_CONTRACT_STATUS,
  type ExternalProviderDashboardVM,
} from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import type { InvoiceFormInputs } from "../../form-validation/invoice-form.schema";
import { formatToCurrency } from "@utils/formatting";
import { PDF_MAX_FILE_SIZE, PDF_FILETYPE } from "@shared-kernel/domain/types/file-validation";
import { BillingAddressField } from "@shared-kernel/primary/shared/billing-address-field";
import { ORGANIZATION_INFO } from "@shared-kernel/domain/organization";
import { IbanInput } from "@shared-kernel/primary/shared/iban-input/iban-input";

interface Props {
  data: ExternalProviderDashboardVM;
  now: Date;
  form: UseFormReturn<InvoiceFormInputs>;
  onSubmit: FormSubmit;
  onUpload: (file: File) => void;
  uploadProcessing: RequestState;
  submitProcessing: RequestState;
}

export const InvoiceSection = ({ data, now, form, onSubmit, onUpload, uploadProcessing, submitProcessing }: Props) => {
  const isContractPendingSignature =
    !data.request.subcontractingContract || data.request.subcontractingContract.status === SUBCONTRACTING_CONTRACT_STATUS.PENDING;

  const {
    watch,
    control,
    formState: { errors },
  } = form;

  const invoiceUrl = watch("invoiceUrl");
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-white">Facture</h4>
      </div>

      {isContractPendingSignature && (
        <Alert variant="error" className="text-sm">
          Veuillez d'abord signer le contrat de sous-traitance avant de soumettre votre facture.
        </Alert>
      )}

      <div className="space-y-3 rounded-lg bg-card p-4">
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4 text-foreground">
            {/* Banking Info Section */}
            <div className="space-y-3">
              <FormField
                control={control}
                name="iban"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Votre IBAN</FormLabel>
                    <FormControl>
                      <IbanInput {...field} placeholder="FR76..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Invoice Info Section */}
            {/* Client Info Section */}
            {data.request.organization && <BillingAddressField organization={data.request.organization} />}
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={control}
                name="invoiceNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>N° de facture</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ex: FAC-2024-001" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Montant</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min={MIN_BATCH_PRICE}
                        max={MAX_BATCH_PRICE}
                        placeholder="0.00"
                        {...field}
                        onChange={e => field.onChange(e.target.valueAsNumber)}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-xs text-muted-foreground">Prix de la formation: {formatToCurrency(data.training.price)}</p>
                  </FormItem>
                )}
              />
            </div>

            {/* Invoice Dates */}
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={control}
                name="invoiceDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date de facturation</FormLabel>
                    <FormControl>
                      <PopoverCalendar
                        now={now}
                        value={field.value}
                        onChange={field.onChange}
                        minDate={getMinFiscalPeriodDate(now)}
                        maxDate={now}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="invoiceDeadline"
                render={({ field }) => {
                  const invoiceDate = watch("invoiceDate");
                  return (
                    <FormItem>
                      <FormLabel>Date d'échéance</FormLabel>
                      <FormControl>
                        <PopoverCalendar
                          now={now}
                          value={field.value}
                          onChange={field.onChange}
                          minDate={invoiceDate ? new Date(invoiceDate.getTime() + 86400000) : undefined}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>

            {/* File Upload */}
            {data.request.organization && (
              <Alert variant="warning" className="text-sm">
                {(() => {
                  const org = ORGANIZATION_INFO[data.request.organization];
                  return `Merci de bien adresser la facture à ${org.name} (${org.address}, ${org.postalCode} ${org.city}). Si le destinataire ne correspond pas, cela ralentira le règlement.`;
                })()}
              </Alert>
            )}
            <UploadButton
              label="Facture (PDF)"
              onFileSelect={onUpload}
              accept={PDF_FILETYPE}
              maxSize={PDF_MAX_FILE_SIZE}
              value={invoiceUrl}
              processing={uploadProcessing}
              helpText="Taille maximale: 10 Mo"
              error={errors.invoiceUrl?.message}
            />

            {/* Submit Button */}
            <Button type="submit" disabled={isContractPendingSignature || submitProcessing === "pending" || uploadProcessing === "pending"}>
              {submitProcessing === "pending" ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                "Soumettre la facture"
              )}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};
